/**
 * This file is part of vue-boilerplate.
 * @link     : https://haiman.io/
 * @author   : hightman (hightman@cloud-sun.com)
 * @copyright: Copyright (c) 2024 TwoMice Studio.
 */

import * as api from '@/api/message';
import { getPeek } from '@/api/user';
import { MESSAGE } from '@/store/types';
import { finalizeAction, ActionType, StoreActionEnv } from '@/store/actions';
import { Message, MessageContact } from '@/api/types/message';
import { UserBasic } from '@/api/types/user';
import { findCache, saveCache, StoreDataCache } from '../cache';

export interface StoreUserMessageInfo {
  loading: boolean;
  list: Message[];
  point: number;
  noMore: boolean;
  updateTime: number;
  user?: UserBasic;
}

export interface StoreUserMessageState {
  cache: StoreDataCache<StoreUserMessageInfo>[];
  contacts: MessageContact[];
}

const getDefault = (): StoreUserMessageInfo => ({
  loading: false,
  list: [],
  point: 0,
  noMore: false,
  updateTime: 0,
});

export default {
  namespaced: true,
  state: {
    cache: [],
    contacts: [],
  },
  getters: {},
  actions: {
    [MESSAGE.GET_CONTACT_LIST](
      { state, commit }: StoreActionEnv<StoreUserMessageState>,
      { action: rawAction }: { action: ActionType } = { action: 'reload' },
    ) {
      const action = finalizeAction(rawAction, state.contacts.length > 0);
      if (action) {
        api.getMessageContacts().then((res) => {
          commit(MESSAGE.GET_CONTACT_LIST, { data: res.data });
        });
      }
      return Promise.resolve();
    },
    [MESSAGE.GET_MESSAGE_LIST](
      { state, commit, dispatch }: StoreActionEnv<StoreUserMessageState>,
      { action: rawAction, xid }: { action: ActionType; xid: string },
    ) {
      const cache = findCache(state.cache, xid);
      const action = finalizeAction(rawAction, !!cache, cache?.noMore);
      if (action && (!cache || !cache.loading)) {
        commit(MESSAGE.GET_MESSAGE_LIST, { status: 'start', xid });
        const point = cache && action === 'more' ? cache.point : void 0;
        return new Promise<void>((resolve, reject) => {
          const ps: Promise<void>[] = [];
          if (action === 'reload' && xid.startsWith('uX')) {
            const p = getPeek({ xid, silent: true }).then((res) => {
              commit(MESSAGE.GET_MESSAGE_LIST, { status: 'success_user', xid, data: res.data });
            }).catch(reject);
            ps.push(p);
          }
          ps.push(api.getMessages(xid, point).then((res) => {
            commit(MESSAGE.GET_MESSAGE_LIST, { status: 'success', action, xid, data: res.data, nextPoint: res.extra && res.extra.nextPoint });
          }));
          Promise.all(ps).then(() => {
            resolve();
          }).catch((err) => {
            commit(MESSAGE.GET_MESSAGE_LIST, { status: 'failure', xid });
            reject(err);
          });
        });
      }
      // 如果读取的是缓存，则强制取一次最新数据
      if (cache && !cache.loading) {
        dispatch(MESSAGE.GET_MESSAGE_LIST_NEW, { xid });
      }
      return Promise.resolve();
    },
    [MESSAGE.GET_MESSAGE_LIST_NEW](
      { state, commit }: StoreActionEnv<StoreUserMessageState>,
      { xid }: { xid: string },
    ) {
      const cache = findCache(state.cache, xid);
      if (!cache) {
        return Promise.reject(new Error('Cache unavailable'));
      }
      if (cache.loading) {
        return Promise.resolve();
      }
      commit(MESSAGE.GET_MESSAGE_LIST, { status: 'start', xid });
      const point = cache.list.length > 0 ? 0 - cache.list[0].id : void 0;
      return new Promise<void>((resolve, reject) => {
        api.getMessages(xid, point).then((res) => {
          commit(MESSAGE.GET_MESSAGE_LIST_NEW, { xid, data: res.data });
          resolve();
        }).catch(reject);
      });
    },
    [MESSAGE.POST_MESSAGE](
      { dispatch }: StoreActionEnv,
      { xid, data }: { xid: string; data: any },
    ) {
      return new Promise<void>((resolve, reject) => {
        api.postMessage(xid, data).then(() => {
          dispatch(MESSAGE.GET_MESSAGE_LIST_NEW, { xid }).then(resolve).catch(reject);
        }).catch(reject);
      });
    },
    [MESSAGE.DEL_MESSAGE](
      { commit }: StoreActionEnv,
      { xid, id }: { xid: string; id: number },
    ) {
      return api.delMessage(id).then(() => {
        commit(MESSAGE.DEL_MESSAGE, { xid, id });
      });
    },
  },
  mutations: {
    [MESSAGE.GET_CONTACT_LIST](state, { data }) {
      state.contacts = data;
    },
    [MESSAGE.GET_MESSAGE_LIST](state: StoreUserMessageState, { status, action, xid, data, nextPoint }) {
      const cache = findCache(state.cache, xid) || getDefault();
      cache.loading = status === 'start';
      if (status === 'success') {
        cache.list = action === 'reload' ? data : cache.list.concat(data);
        cache.noMore = !nextPoint;
        cache.point = nextPoint;
        cache.updateTime = new Date().valueOf();
      } else if (status === 'success_user') {
        cache.user = data;
      }
      saveCache(state.cache, xid, cache);
    },
    [MESSAGE.GET_MESSAGE_LIST_NEW](state: StoreUserMessageState, { xid, data }) {
      const cache = findCache(state.cache, xid);
      if (cache) {
        cache.loading = false;
        cache.list = data.concat(cache.list);
        cache.updateTime = new Date().valueOf();
        saveCache(state.cache, xid, cache);
      }
    },
    [MESSAGE.DEL_MESSAGE](state: StoreUserMessageState, { xid, id }) {
      const cache = findCache(state.cache, xid);
      if (cache) {
        cache.list = cache.list.filter(item => item.id !== id);
        saveCache(state.cache, xid, cache);
      }
    },
  },
};
